import {
    Button,
    Form,
    Select,
    Table,
    Space,
    Tooltip,
    Input,
    Checkbox,
    Alert,
} from 'antd';
import { CopyTwoTone } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import {
    DefaultPaginationConfig,
    DEFAULT_PAGE_SIZE,
} from '../../constants/pagination';

import { RUT_NOT_VALID } from '../../constants/feedback';
import {
    TIPO_OPORTUNIDADES_KVP_ENDPOINT,
    getAllTipoOportunidadesAsKvpList,
} from '../../services/api/tipo-oportunidad';
import {
    COUNT_OPORTUNIDADES_ENDPOINT,
    DETALLE_OPORTUNIDAD_ENDPOINT,
    OPORTUNIDADES_ENDPOINT,
    countOportunidades,
    getAllOportunidades,
    getDetalleOportunidad,
} from '../../services/api/oportunidad';
import { clearRut, formatRut, validateRut } from '../../utils/rut';
import { ITEM_TODOS } from '../../constants/select';
import OportunidadItem from './oportunidad-item';

const { Option } = Select;
const { Column } = Table;

const itemLayout = {
    labelCol: { span: 24 },
};

const initialFilters = {
    textoBusqueda: '',
    rutOrganismo: '',
    idTipoOportunidad: '',
    flagSinPostulacion: true,
    sortField: 'fechaCierre',
    sortOrder: 'ascend',
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
};

const OportunidadList = () => {
    const [filters, setFilters] = useState({ ...initialFilters });
    const [selectedFilters, setSelectedFilters] = useState({
        ...initialFilters,
    });
    const [selectedId, setSelectedId] = useState(0);
    const [countCompraAgil, setCountCompraAgil] = useState('0');
    const [countLicitacion, setCountLicitacion] = useState('0');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { data: tipoOportunidades } = useSWRImmutable(
        TIPO_OPORTUNIDADES_KVP_ENDPOINT,
        () => getAllTipoOportunidadesAsKvpList()
    );

    const { data: oportunidades, isLoading } = useSWR(
        [OPORTUNIDADES_ENDPOINT, filters],
        ([_, filters]) => getAllOportunidades(filters)
    );

    const { data: qOportunidades } = useSWR(
        [COUNT_OPORTUNIDADES_ENDPOINT],
        () => countOportunidades()
    );

    const { data: detalleOportunidades, isLoading: isLoadingDetalle } = useSWR(
        selectedId > 0 ? [DETALLE_OPORTUNIDAD_ENDPOINT, selectedId] : null,
        ([_, selectedId]) => getDetalleOportunidad(selectedId)
    );

    const [form] = Form.useForm();

    useEffect(() => {
        if (qOportunidades?.data) {
            setCountCompraAgil(
                qOportunidades.data.countCompraAgil.toLocaleString()
            );

            setCountLicitacion(
                qOportunidades.data.countLicitacion.toLocaleString()
            );
        } else {
            setCountCompraAgil('0');
            setCountLicitacion('0');
        }
    }, [qOportunidades]);

    const handleTableChange = (pagination, _, sorter) => {
        const params = {
            ...filters,
            pageNumber: Number(pagination.current),
            pageSize: Number(pagination.pageSize),
            sortField: sorter.columnKey,
            sortOrder: sorter.order,
        };

        setSelectedFilters({ ...params });
        setFilters({ ...params });
    };

    const getActionColumn = (oportunidad) => {
        return (
            <Space size={0}>
                <Tooltip title='Detalle'>
                    <CopyTwoTone
                        onClick={() => handleDetailClick(oportunidad)}
                    />
                </Tooltip>
            </Space>
        );
    };

    const handleShowSizeChange = (current, pageSize) => {
        setSelectedFilters({
            ...filters,
            pageSize: pageSize,
        });
    };

    const handleReset = async () => {
        form.resetFields();

        setSelectedFilters({ ...initialFilters });
        setFilters({ ...initialFilters });
    };

    const handleFinish = async (values) => {
        const params = {
            ...selectedFilters,
            textoBusqueda: values.textoBusqueda,
            flagSinPostulacion: values.flagSinPostulacion,
            rutOrganismo: values.rutOrganismo,
            idTipoOportunidad: values.idTipoOportunidad || null,
            pageNumber: 1,
        };

        setFilters(params);
    };

    const handleRutOrganismoBlur = (e) => {
        const formattedRut = formatRut(e.target.value);

        if (formattedRut) {
            form.setFieldValue('rutOrganismo', formatRut(e.target.value));
        }
    };

    const handleRutOrganismoFocus = (e) => {
        form.setFieldValue('rutOrganismo', clearRut(e.target.value));
    };

    const columns = [
        {
            title: 'Descripción',
            dataIndex: 'referenciaProducto1',
            sorter: (a, b) =>
                a.referenciaProducto1 < b.referenciaProducto1 ? -1 : 1,
            width: '80%',
        },
        {
            title: 'Cantidad',
            dataIndex: 'cantidad',
            sorter: (a, b) => a.cantidad - b.cantidad,
            width: '20%',
        },
    ];

    const handleTipoChange = (newValue) => {
        if (newValue === undefined) {
            form.setFieldsValue({ idTipoOportunidad: 0 });
        }
    };

    const handleRowClick = (record, rowIndex) => {
        setSelectedId(record.idOportunidad);
    };

    const handleDetailClick = (oportunidad) => {
        setSelectedId(oportunidad.idOportunidad);
        setIsModalOpen(true);
    };

    const handleCancelModal = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            <div className='row pt-3'>
                <div className='col-lg-12'>
                    <ul className='breadcrumb shadow-sm rounded-2'>
                        <li>
                            <h6>Buscador de oportunidades</h6>
                        </li>
                    </ul>
                </div>

                <div className='col-lg-12'>
                    <div className='card rounded-4 shadow'>
                        <h6 className='txt-color-primary pt-3 border-bottom mx-3'>
                            Filtros
                        </h6>

                        <div className='card-body'>
                            <Form
                                form={form}
                                onFinish={handleFinish}
                                initialValues={{
                                    flagSinPostulacion: true,
                                    idTipoOportunidad: 0,
                                }}
                            >
                                <div className='row pl-2 pr-2'>
                                    <div className='col-lg-3 pt-2 pb-3'>
                                        <Form.Item
                                            name='textoBusqueda'
                                            label='Texto de búsqueda'
                                            {...itemLayout}
                                            rules={[
                                                {
                                                    whitespace: true,
                                                },
                                            ]}
                                        >
                                            <Input maxLength={50} allowClear />
                                        </Form.Item>
                                    </div>

                                    <div className='col-lg-3 pt-2 pb-3'>
                                        <Form.Item
                                            name='rutOrganismo'
                                            label='Cliente'
                                            {...itemLayout}
                                            rules={[
                                                {
                                                    whitespace: true,
                                                },
                                                () => ({
                                                    validator(_, value) {
                                                        if (
                                                            !value ||
                                                            validateRut(value)
                                                        ) {
                                                            return Promise.resolve();
                                                        }
                                                        return Promise.reject(
                                                            new Error(
                                                                RUT_NOT_VALID
                                                            )
                                                        );
                                                    },
                                                }),
                                            ]}
                                        >
                                            <Input
                                                maxLength={12}
                                                allowClear
                                                onBlur={handleRutOrganismoBlur}
                                                onFocus={
                                                    handleRutOrganismoFocus
                                                }
                                            />
                                        </Form.Item>
                                    </div>

                                    <div className='col-lg-3 pt-2 pb-3'>
                                        <Form.Item
                                            name='idTipoOportunidad'
                                            label='Tipo'
                                            {...itemLayout}
                                        >
                                            <Select
                                                className='w-100'
                                                allowClear
                                                onChange={handleTipoChange}
                                            >
                                                <Option value={0}>
                                                    {ITEM_TODOS}
                                                </Option>
                                                {tipoOportunidades?.data.map(
                                                    (tipoOportunidad) => (
                                                        <Option
                                                            key={
                                                                tipoOportunidad.idTipoOportunidad
                                                            }
                                                            value={
                                                                tipoOportunidad.idTipoOportunidad
                                                            }
                                                        >
                                                            {
                                                                tipoOportunidad.descripcion
                                                            }
                                                        </Option>
                                                    )
                                                )}
                                            </Select>
                                        </Form.Item>
                                    </div>

                                    <div className='col-lg-3 pt-2 pb-3'>
                                        <Form.Item
                                            name='flagSinPostulacion'
                                            label='Ver solo sin postulación'
                                            valuePropName='checked'
                                            {...itemLayout}
                                        >
                                            <Checkbox></Checkbox>
                                        </Form.Item>
                                    </div>

                                    <div className='col-lg-12 text-end'>
                                        <Form.Item className='mb-0'>
                                            <Space>
                                                <Button
                                                    type='primary'
                                                    htmlType='submit'
                                                >
                                                    Filtrar
                                                </Button>
                                                <Button onClick={handleReset}>
                                                    Limpiar
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </div>
                                </div>
                            </Form>
                        </div>
                    </div>
                </div>
            </div>

            <div className='row pt-4 pb-5'>
                <div className='col-lg-12'>
                    <div className='card rounded-4 shadow'>
                        <h6 className='txt-color-primary pt-3 border-bottom mx-3'>
                            Oportunidades
                        </h6>

                        <div className='card-body'>
                            <div className='row pl-2 pr-2'>
                                <div className='col-lg-6 pt-2 pb-3'>
                                    <Alert
                                        message={`Cantidad de Compra Ágil: ${countCompraAgil}`}
                                        type='info'
                                        showIcon
                                    />

                                    <Table
                                        rowKey={(record) =>
                                            record.idOportunidad
                                        }
                                        dataSource={oportunidades?.data.items}
                                        pagination={{
                                            ...DefaultPaginationConfig,
                                            total: oportunidades?.data
                                                .totalCount,
                                            onShowSizeChange:
                                                handleShowSizeChange,
                                        }}
                                        onChange={handleTableChange}
                                        onRow={(record, rowIndex) => {
                                            return {
                                                onClick: () =>
                                                    handleRowClick(
                                                        record,
                                                        rowIndex
                                                    ),
                                            };
                                        }}
                                        loading={isLoading}
                                        className='mt-3'
                                    >
                                        <Column
                                            title='N° Ref'
                                            dataIndex='codigoOp'
                                            key='codigoOp'
                                            sorter
                                            width='30%'
                                        />
                                        <Column
                                            title='Fin'
                                            dataIndex='fechaCierre'
                                            key='fechaCierre'
                                            sorter
                                            width='20%'
                                            render={(value) =>
                                                `${new Date(
                                                    value
                                                ).toLocaleDateString()} ${new Date(
                                                    value
                                                ).toLocaleTimeString()}`
                                            }
                                        />
                                        <Column
                                            title='Monto'
                                            dataIndex='presupuesto'
                                            key='presupuesto'
                                            sorter
                                            width='20%'
                                            render={(value) =>
                                                value.toLocaleString()
                                            }
                                        />
                                        <Column
                                            title='Lin'
                                            dataIndex='qlineas'
                                            key='qlineas'
                                            sorter
                                            width='20%'
                                        />
                                        <Column
                                            title='Acción'
                                            render={(oportunidad) =>
                                                getActionColumn(oportunidad)
                                            }
                                        />
                                    </Table>
                                </div>
                                <div className='col-lg-6 pt-2 pb-3'>
                                    <Alert
                                        message={`Cantidad de Licitaciones: ${countLicitacion}`}
                                        type='info'
                                        showIcon
                                    />

                                    <Table
                                        rowKey={(record) =>
                                            record.idDetalleOportunidad
                                        }
                                        dataSource={detalleOportunidades?.data}
                                        columns={columns}
                                        pagination={{
                                            ...DefaultPaginationConfig,
                                        }}
                                        loading={isLoadingDetalle}
                                        className='mt-3'
                                    ></Table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <OportunidadItem
                    isModalOpen={isModalOpen}
                    idOportunidad={selectedId}
                    onCancel={handleCancelModal}
                />
            )}
        </>
    );
};

export default OportunidadList;
